import React, { useState, memo } from 'react';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER, MEDIA_KIND_META, mediaKindOfFile } from '../utils/parameterUtils';

/**
 * ImageUploadSection - Handles media upload UI for parameters
 * Displays upload areas, uploaded media, and handles disabled states with drag-drop support
 * Memoized to prevent unnecessary re-renders
 *
 * Each param carries a `kind` ('image' | 'video' | 'audio') from
 * classifyMediaParam; the accept attribute, preview element and copy all
 * follow it. Name is kept for the existing import sites.
 */
const ImageUploadSection = memo(function ImageUploadSection({
  imageParams,
  getImagesForParam,
  handleImageUpload,
  handleRemoveImageFromParam,
  disabledParams,
  isUploading,
  uploadError,
  fileInputRefs,
}) {
  const [dragOver, setDragOver] = useState(null); // Track which param has drag over
  const [rejected, setRejected] = useState(null); // Param that just refused a wrong-kind drop

  if (!imageParams || imageParams.length === 0) return null;

  // Handle drag events
  const handleDragOver = (e, paramName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(paramName);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
  };

  const handleDrop = (e, param) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    // Must match THIS slot's kind. Previously hardcoded to image/, which
    // silently discarded any video or audio drop with no feedback.
    if (mediaKindOfFile(file) !== (param.kind || 'image')) {
      setRejected(param.paramName);
      setTimeout(() => setRejected(null), 3000);
      return;
    }
    handleImageUpload(param, file);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Upload failure (size cap, unsupported type, network). Shown once for
          the section — the upload state is not tracked per-param here. */}
      {uploadError && (
        <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8 }} role="alert">
          {uploadError}
        </div>
      )}
      {imageParams.map(param => {
        const images = getImagesForParam(param.paramName);
        const canAddMore = param.type === 'single' ? images.length === 0 : images.length < param.max;
        const isDisabled = !!disabledParams[param.paramName];
        const disabledReason = disabledParams[param.paramName]?.reason;
        const isDragOver = dragOver === param.paramName;
        const kind = param.kind || 'image';
        const meta = MEDIA_KIND_META[kind];
        const wasRejected = rejected === param.paramName;

        return (
          <div 
            key={param.paramName} 
            style={{ marginBottom: 12, opacity: isDisabled ? 0.5 : 1 }}
            role="group"
            aria-labelledby={`label-${param.paramName}`}
          >
            {/* Image param label with max count */}
            <div 
              id={`label-${param.paramName}`}
              style={{ 
                fontSize: 13, 
                fontWeight: 500, 
                color: '#fff', 
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {param.title}
                {param.required && <span style={{ color: PRIMARY }} aria-hidden="true">*</span>}
                {param.required && <span className="sr-only">(required)</span>}
              </div>
              {param.type === 'array' && (
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  {images.length}/{param.max} {meta.plural}
                </span>
              )}
            </div>

            {/* Disabled reason message */}
            {isDisabled && (
              <div style={{ 
                fontSize: 12, 
                color: '#f59e0b', 
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {disabledReason}
              </div>
            )}

            {/* Wrong-kind drop feedback */}
            {wasRejected && (
              <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 8 }} role="status">
                That file isn’t {meta.article} {meta.label} — “{param.title}” takes {meta.plural}.
              </div>
            )}


            {/* Hidden file input */}
            <input
              ref={el => { fileInputRefs.current[param.paramName] = el; }}
              type="file"
              accept={meta.accept}
              style={{ display: 'none' }}
              aria-label={`Upload ${meta.label} for ${param.title}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(param, file);
                e.target.value = '';
              }}
            />
            
            {images.length > 0 ? (
              // Show uploaded images
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      borderRadius: 12,
                      overflow: 'hidden',
                    }}
                  >
                    {kind === 'image' && (
                      <img
                        src={img}
                        alt={`Upload ${idx + 1} for ${param.title}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                    {kind === 'video' && (
                      <video
                        src={img}
                        muted
                        playsInline
                        controls
                        preload="metadata"
                        aria-label={`Video ${idx + 1} for ${param.title}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000', display: 'block' }}
                      />
                    )}
                    {kind === 'audio' && (
                      <div
                        title={img}
                        aria-label={`Audio ${idx + 1} for ${param.title}`}
                        style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: PRIMARY_LIGHT, color: PRIMARY, fontSize: 26 }}
                      >♪</div>
                    )}
                    <button
                      onClick={() => handleRemoveImageFromParam(param.paramName, idx)}
                      aria-label={`Remove ${meta.label} ${idx + 1}`}
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
                {canAddMore && (
                  <button
                    onClick={() => fileInputRefs.current[param.paramName]?.click()}
                    disabled={isUploading || isDisabled}
                    aria-label={`Add another ${meta.label} for ${param.title}`}
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 12,
                      border: `2px dashed ${PRIMARY_BORDER}`,
                      background: PRIMARY_LIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="2" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              // Upload area with drag-drop support
              <button
                onClick={() => !isDisabled && fileInputRefs.current[param.paramName]?.click()}
                onDragOver={(e) => !isDisabled && handleDragOver(e, param.paramName)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => !isDisabled && handleDrop(e, param)}
                disabled={isUploading || isDisabled}
                style={{
                  width: '100%',
                  padding: '24px 16px',
                  background: isDragOver 
                    ? `${PRIMARY}25` 
                    : isDisabled 
                      ? 'rgba(255,255,255,0.02)' 
                      : PRIMARY_LIGHT,
                  border: `2px dashed ${
                    isDragOver 
                      ? PRIMARY 
                      : isDisabled 
                        ? 'rgba(255,255,255,0.1)' 
                        : PRIMARY_BORDER
                  }`,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  marginBottom: 16,
                  transition: 'all 0.2s ease',
                  transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isDragOver ? `0 0 20px ${PRIMARY}30` : 'none',
                }}
              >
                <svg 
                  width="32" 
                  height="32" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke={isDisabled ? '#6b7280' : PRIMARY} 
                  strokeWidth="1.5"
                  style={{ 
                    transition: 'transform 0.2s ease',
                    transform: isDragOver ? 'translateY(-4px)' : 'translateY(0)',
                  }}
                >
                  {isDragOver ? (
                    // Drop icon when dragging
                    <>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </>
                  ) : (
                    // Default upload icon
                    <>
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                      <line x1="12" y1="9" x2="12" y2="15" strokeWidth="2" />
                      <line x1="9" y1="12" x2="15" y2="12" strokeWidth="2" />
                    </>
                  )}
                </svg>
                <span style={{ 
                  fontSize: 14, 
                  fontWeight: 500, 
                  color: isDisabled ? '#6b7280' : PRIMARY,
                  transition: 'all 0.2s ease',
                }}>
                  {isDragOver
                    ? `Drop ${meta.label} here`
                    : isUploading
                      ? 'Uploading...'
                      : isDisabled
                        ? 'Disabled'
                        : imageParams.length === 1 && kind === 'image'
                          ? 'Choose an Image or Take a Picture'
                          : `Upload ${param.title}`}
                </span>
                {!isDisabled && !isDragOver && (
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    or drag and drop
                  </span>
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
});

export default ImageUploadSection;
